import React, { useState, useEffect, useMemo } from 'react';
import { 
  ChevronRight, 
  ChevronLeft,
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  Search, 
  Building2, 
  Loader2,
  RefreshCw
} from 'lucide-react';
import * as settingService from '../../../services/settingServices';
import type { 
  StateItem, 
  DistrictItem, 
  TownshipItem, 
  OfficeItem 
} from '../../../services/settingServices';
import { GeoLevel, type DepartmentType } from '../../../types/database';
import { NotificationToggle } from '../component/NotificationToggle';
import type { User } from '../../../types/user';

export type GeoNavLevel = 'state' | 'district' | 'township';

export interface BaseGeoItem {
  id: string;
  name: string;
  parentId?: string;
  type?: GeoNavLevel;
}

const LEVEL_LABELS: Record<GeoNavLevel, string> = {
  state: 'တိုင်းဒေသကြီး / ပြည်နယ်',
  district: 'ခရိုင်',
  township: 'မြို့နယ်',
};

const DEPARTMENT_OPTIONS: DepartmentType[] = [
  'ATTORNEY_GENERAL',
  'CIVIL',
  'CRIMINAL',
  'LEGAL_ADVISORY',
  'LITIGATION',
  'PROSECUTION',
  'ADMINISTRATION',
  'FINANCE',
  'HUMAN_RESOURCE',
  'TRAINING',
];

export const LocationSetup: React.FC = () => {
  // --- Data States ---
  const [offices, setOffices] = useState<OfficeItem[]>([]);
  const [states, setStates] = useState<StateItem[]>([]);
  const [districts, setDistricts] = useState<DistrictItem[]>([]);
  const [townships, setTownships] = useState<TownshipItem[]>([]);

  // --- Office Pagination State ---
  const [officePage, setOfficePage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // --- Search & Navigation States ---
  const [officeSearchQuery, setOfficeSearchQuery] = useState('');
  const [geoSearchQuery, setGeoSearchQuery] = useState('');
  const [currentLevel, setCurrentLevel] = useState<GeoNavLevel>('state');
  const [selectedState, setSelectedState] = useState<StateItem | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<DistrictItem | null>(null);

  // --- UI & Loader States ---
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Office Modal State ---
  const [isOfficeModalOpen, setIsOfficeModalOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState<OfficeItem | null>(null);
  const [officeForm, setOfficeForm] = useState<{
    name: string;
    code: string;
    phone: string;
    department: DepartmentType;
    level: GeoLevel;
    townshipId: string;
  }>({
    name: '',
    code: '',
    phone: '',
    department: 'ADMINISTRATION',
    level: GeoLevel.TOWNSHIP,
    townshipId: '',
  });

  // --- Geo Modal State ---
  const [isGeoModalOpen, setIsGeoModalOpen] = useState(false);
  const [editingGeoItem, setEditingGeoItem] = useState<BaseGeoItem | null>(null);
  const [geoNameInput, setGeoNameInput] = useState('');

  // --- Initial Data Loader ---
  const loadInitialData = async () => {
    try {
      const res = await settingService.getSettings();
      if (res?.data) {
        setStates(res.data.states || []);
        setDistricts(res.data.districts || []);
        setTownships(res.data.townships || []);
        setOffices(res.data.offices || []);
      }
    } catch (error) {
      console.error('Failed to load initial settings:', error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadInitialData();
      setLoading(false);
    };
    init();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadInitialData();
    setIsRefreshing(false);
  };

  // Reset pagination when search query changes
  useEffect(() => {
    setOfficePage(1);
  }, [officeSearchQuery]);

  // --- Derived Calculations ---
  const isGeoSearching = geoSearchQuery.trim().length > 0;

  const currentGeoItems = useMemo<BaseGeoItem[]>(() => {
    if (isGeoSearching) {
      const q = geoSearchQuery.toLowerCase();
      const matchedStates = states
        .filter((s) => s.name.toLowerCase().includes(q))
        .map((s) => ({ id: s.id, name: s.name, type: 'state' as GeoNavLevel }));

      const matchedDistricts = districts
        .filter((d) => d.name.toLowerCase().includes(q))
        .map((d) => ({ id: d.id, name: d.name, parentId: d.stateId, type: 'district' as GeoNavLevel }));

      const matchedTownships = townships
        .filter((t) => t.name.toLowerCase().includes(q))
        .map((t) => ({ id: t.id, name: t.name, parentId: t.districtId, type: 'township' as GeoNavLevel }));

      return [...matchedStates, ...matchedDistricts, ...matchedTownships];
    }

    switch (currentLevel) {
      case 'state':
        return states.map((s) => ({ id: s.id, name: s.name, type: 'state' }));
      case 'district':
        return districts
          .filter((d) => d.stateId === selectedState?.id)
          .map((d) => ({ id: d.id, name: d.name, parentId: d.stateId, type: 'district' }));
      case 'township':
        return townships
          .filter((t) => t.districtId === selectedDistrict?.id)
          .map((t) => ({ id: t.id, name: t.name, parentId: t.districtId, type: 'township' }));
      default:
        return [];
    }
  }, [currentLevel, states, districts, townships, selectedState, selectedDistrict, geoSearchQuery, isGeoSearching]);

  const filteredOffices = useMemo(() => {
    if (!officeSearchQuery.trim()) return offices;
    const q = officeSearchQuery.toLowerCase();
    return offices.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.code.toLowerCase().includes(q) ||
        o.township?.name.toLowerCase().includes(q)
    );
  }, [offices, officeSearchQuery]);

  // Office Pagination Derived States
  const totalOfficePages = Math.ceil(filteredOffices.length / itemsPerPage) || 1;
  const paginatedOffices = useMemo(() => {
    const start = (officePage - 1) * itemsPerPage;
    return filteredOffices.slice(start, start + itemsPerPage);
  }, [filteredOffices, officePage, itemsPerPage]);

  // --- Office Handlers ---
  const handleOpenAddOfficeModal = () => {
    setEditingOffice(null);
    setOfficeForm({
      name: '',
      code: `OFF-${Date.now().toString().slice(-4)}`,
      phone: '',
      department: 'ADMINISTRATION',
      level: GeoLevel.TOWNSHIP,
      townshipId: townships[0]?.id || '',
    });
    setIsOfficeModalOpen(true);
  };

  const handleOpenEditOfficeModal = (office: OfficeItem) => {
    setEditingOffice(office);
    setOfficeForm({
      name: office.name,
      code: office.code,
      phone: office.phone,
      department: office.department,
      level: office.level,
      townshipId: office.townshipId || '',
    });
    setIsOfficeModalOpen(true);
  };

  const handleOfficeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingOffice) {
        await settingService.updateOffice(editingOffice.id, officeForm);
      } else {
        await settingService.createOffice(officeForm);
      }
      await loadInitialData();
      setIsOfficeModalOpen(false);
    } catch (err) {
      console.error('Failed to save office:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteOffice = async (office: OfficeItem) => {
    if (confirm(`"${office.name}" ရုံးခွဲကို ဖျက်ရန် သေချာပါသလား?`)) {
      setLoading(true);
      try {
        await settingService.deleteOffice(office.id);
        await loadInitialData();
      } catch (err) {
        console.error('Failed to delete office:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  // --- Geo Navigation Handlers ---
  const handleGeoRowClick = (item: BaseGeoItem) => {
    const targetLevel = item.type || currentLevel;

    if (targetLevel === 'state') {
      const match = states.find((s) => s.id === item.id) || null;
      setSelectedState(match);
      setCurrentLevel('district');
      setSelectedDistrict(null);
      setGeoSearchQuery('');
    } else if (targetLevel === 'district') {
      const matchDistrict = districts.find((d) => d.id === item.id) || null;
      if (matchDistrict) {
        const parentState = states.find((s) => s.id === matchDistrict.stateId) || null;
        setSelectedState(parentState);
        setSelectedDistrict(matchDistrict);
      }
      setCurrentLevel('township');
      setGeoSearchQuery('');
    }
  };

  const handleBackNav = () => {
    if (currentLevel === 'township') {
      setCurrentLevel('district');
      setSelectedDistrict(null);
    } else if (currentLevel === 'district') {
      setCurrentLevel('state');
      setSelectedState(null);
    }
  };

  // --- Geo CRUD Handlers ---
  const handleOpenGeoAddModal = () => {
    setEditingGeoItem(null);
    setGeoNameInput('');
    setIsGeoModalOpen(true);
  };

  const handleOpenGeoEditModal = (e: React.MouseEvent, item: BaseGeoItem) => {
    e.stopPropagation();
    setEditingGeoItem(item);
    setGeoNameInput(item.name);
    setIsGeoModalOpen(true);
  };

  const handleGeoFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!geoNameInput.trim()) return;

    const activeLevel = editingGeoItem?.type || currentLevel;
    setIsSubmitting(true);

    try {
      if (editingGeoItem) {
        if (activeLevel === 'state') {
          await settingService.updateState(editingGeoItem.id, { name: geoNameInput.trim() });
        } else if (activeLevel === 'district') {
          await settingService.updateDistrict(editingGeoItem.id, { name: geoNameInput.trim() });
        } else if (activeLevel === 'township') {
          await settingService.updateTownship(editingGeoItem.id, { name: geoNameInput.trim() });
        }
      } else {
        if (activeLevel === 'state') {
          await settingService.createState({ name: geoNameInput.trim() });
        } else if (activeLevel === 'district') {
          if (selectedState) {
            await settingService.createDistrict({ name: geoNameInput.trim(), stateId: selectedState.id });
          }
        } else if (activeLevel === 'township') {
          if (selectedDistrict) {
            await settingService.createTownship({ name: geoNameInput.trim(), districtId: selectedDistrict.id });
          }
        }
      }

      await loadInitialData();
      setIsGeoModalOpen(false);
      setGeoNameInput('');
    } catch (err) {
      console.error('Failed to submit geo item:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteGeoClick = async (e: React.MouseEvent, item: BaseGeoItem) => {
    e.stopPropagation();
    const activeLevel = item.type || currentLevel;

    if (confirm(`"${item.name}" ကို ဖျက်ရန် သေချာပါသလား?`)) {
      setLoading(true);
      try {
        if (activeLevel === 'state') await settingService.deleteState(item.id);
        if (activeLevel === 'district') await settingService.deleteDistrict(item.id);
        if (activeLevel === 'township') await settingService.deleteTownship(item.id);

        await loadInitialData();
      } catch (err) {
        console.error('Failed to delete geo item:', err);
      } finally {
        setLoading(false);
      }
    }
  };

    const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null
  

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-white/50 backdrop-blur-xs flex items-center justify-center z-40">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      )}

      <div className="p-4 bg-white shadow rounded-md">
      <h2 className="text-lg font-semibold mb-4">Preferences</h2>
      <NotificationToggle
        userId={currentUser?.id??""}
        initialEnabled={currentUser?.isNotificationEnabled ?? true}
        onToggleSuccess={(enabled) => {
          console.log(`Notification status changed to: ${enabled}`);
        }}
      />
    </div>

      {/* ========================================================= */}
      {/* SECTION 1: TOP OFFICE MANAGEMENT TABLE                    */}
      {/* ========================================================= */}
      <section className="bg-white shadow-md rounded-lg p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-800">ရုံးခွဲများ စီမံခန့်ခွဲခြင်း (Office Management)</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded-md border border-gray-300 transition-colors disabled:opacity-50"
              title="နောက်ဆုံး Data များ ရယူရန် "
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-blue-600' : ''}`} />
            </button>
            <button
              onClick={handleOpenAddOfficeModal}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors shadow-sm text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> ရုံးခွဲ အသစ်ထည့်ရန်
            </button>
          </div>
        </div>

        {/* Office Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="ရုံးခွဲ အမည်၊ ကုဒ် သို့မဟုတ် တည်နေရာ ရှာဖွေရန်..."
            value={officeSearchQuery}
            onChange={(e) => setOfficeSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Office Table */}
        <div className="overflow-x-auto border border-gray-200 rounded-md">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100 text-gray-700 text-sm font-semibold border-b border-gray-200">
                <th className="p-3">ရုံးခွဲ ကုဒ်</th>
                <th className="p-3">ရုံးခွဲ အမည်</th>
                <th className="p-3">ဌာန (Department)</th>
                <th className="p-3">အဆင့် (Geo Level)</th>
                <th className="p-3">မြို့နယ်/တည်နေရာ</th>
                <th className="p-3 text-right">လုပ်ဆောင်ချက်များ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedOffices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-gray-400 text-sm">
                    ရုံးခွဲ အချက်အလက်များ မရှိသေးပါ။
                  </td>
                </tr>
              ) : (
                paginatedOffices.map((office) => (
                  <tr key={office.id} className="hover:bg-gray-50">
                    <td className="p-3 text-sm font-semibold text-gray-600">{office.code}</td>
                    <td className="p-3 text-sm font-medium text-gray-800">{office.name}</td>
                    <td className="p-3 text-xs font-medium">
                      <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-100">
                        {office.department}
                      </span>
                    </td>
                    <td className="p-3 text-xs font-medium">
                      <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded border border-gray-200">
                        {office.level}
                      </span>
                    </td>
                    <td className="p-3 text-sm text-gray-600">{office.township?.name || 'သတ်မှတ်မထားပါ'}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => handleOpenEditOfficeModal(office)}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-blue-600 transition-colors"
                          title="ပြင်ဆင်ရန်"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteOffice(office)}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-red-600 transition-colors"
                          title="ဖျက်ရန်"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Office Pagination Controls */}
        {filteredOffices.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-3 border-t border-gray-100 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span>တစ်မျက်နှာလျှင် ပြသရန်:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setOfficePage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="ml-2 text-xs text-gray-500">
                စုစုပေါင်း {filteredOffices.length} ခုအနက် {(officePage - 1) * itemsPerPage + 1} မှ{' '}
                {Math.min(officePage * itemsPerPage, filteredOffices.length)} အထိ ပြသနေသည်
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setOfficePage((prev) => Math.max(prev - 1, 1))}
                disabled={officePage === 1}
                className="p-1.5 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
                title="ရှေ့တစ်မျက်နှာ"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="px-2 font-medium">
                {officePage} / {totalOfficePages}
              </span>

              <button
                onClick={() => setOfficePage((prev) => Math.min(prev + 1, totalOfficePages))}
                disabled={officePage >= totalOfficePages}
                className="p-1.5 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
                title="နောက်တစ်မျက်နှာ"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ========================================================= */}
      {/* SECTION 2: GEOGRAPHIC LOCATION MANAGEMENT TABLE          */}
      {/* ========================================================= */}
      <section className="bg-white shadow-md rounded-lg p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {!isGeoSearching && currentLevel !== 'state' && (
              <button
                onClick={handleBackNav}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-800">
              {isGeoSearching ? 'ရှာဖွေမှု ရလဒ်များ' : `${LEVEL_LABELS[currentLevel]} စီမံခန့်ခွဲခြင်း`}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded-md border border-gray-300 transition-colors disabled:opacity-50"
              title="နောက်ဆုံး Data များ ရယူရန် "
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin text-blue-600' : ''}`} />
            </button>
            {!isGeoSearching && (
              <button
                onClick={handleOpenGeoAddModal}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors shadow-sm text-sm font-medium"
              >
                <Plus className="w-4 h-4" /> {LEVEL_LABELS[currentLevel]} အသစ်ထည့်ရန်
              </button>
            )}
          </div>
        </div>

        {/* Geographic Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="တိုင်းဒေသကြီး၊ ခရိုင် သို့မဟုတ် မြို့နယ် ရှာဖွေရန်..."
            value={geoSearchQuery}
            onChange={(e) => setGeoSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-500"
          />
          {isGeoSearching && (
            <button
              onClick={() => setGeoSearchQuery('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Actionable Breadcrumbs */}
        {!isGeoSearching && (
          <nav className="flex items-center gap-2 mb-6 text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
            <button
              onClick={() => {
                setCurrentLevel('state');
                setSelectedState(null);
                setSelectedDistrict(null);
              }}
              className={`hover:underline ${currentLevel === 'state' ? 'font-semibold text-blue-600' : ''}`}
            >
              တိုင်း/ပြည်နယ် အားလုံး
            </button>

            {selectedState && (
              <>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <button
                  onClick={() => {
                    setCurrentLevel('district');
                    setSelectedDistrict(null);
                  }}
                  className={`hover:underline ${currentLevel === 'district' ? 'font-semibold text-blue-600' : ''}`}
                >
                  {selectedState.name}
                </button>
              </>
            )}

            {selectedDistrict && (
              <>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="font-semibold text-blue-600">{selectedDistrict.name}</span>
              </>
            )}
          </nav>
        )}

        {/* Geographic Table */}
        <div className="overflow-x-auto border border-gray-200 rounded-md">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100 text-gray-700 text-sm font-semibold border-b border-gray-200">
                <th className="p-3">အမည်</th>
                {isGeoSearching && <th className="p-3">အမျိုးအစား</th>}
                <th className="p-3 text-right">လုပ်ဆောင်ချက်များ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentGeoItems.length === 0 ? (
                <tr>
                  <td colSpan={isGeoSearching ? 3 : 2} className="p-8 text-center text-gray-400 text-sm">
                    {isGeoSearching
                      ? 'ကိုက်ညီသော အချက်အလက် မတွေ့ရှိပါ။'
                      : `ဤကဏ္ဍတွင် အချက်အလက်မရှိသေးပါ။ "${LEVEL_LABELS[currentLevel]} အသစ်ထည့်ရန်" ကိုနှိပ်၍ စတင်ပါ။`}
                  </td>
                </tr>
              ) : (
                currentGeoItems.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => handleGeoRowClick(item)}
                    className={`group transition-colors ${
                      item.type !== 'township' ? 'cursor-pointer hover:bg-blue-50/50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="p-3 text-sm font-medium text-gray-800 flex items-center justify-between">
                      {item.name}
                      {item.type !== 'township' && (
                        <span className="text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                          {item.type === 'state' ? LEVEL_LABELS['district'] : LEVEL_LABELS['township']}များ ကြည့်ရှုရန်{' '}
                          <ChevronRight className="w-3 h-3" />
                        </span>
                      )}
                    </td>
                    {isGeoSearching && (
                      <td className="p-3 text-sm text-gray-500">
                        <span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-semibold uppercase">
                          {LEVEL_LABELS[item.type || 'state']}
                        </span>
                      </td>
                    )}
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={(e) => handleOpenGeoEditModal(e, item)}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-blue-600 transition-colors"
                          title="ပြင်ဆင်ရန်"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteGeoClick(e, item)}
                          className="p-1.5 hover:bg-gray-100 rounded text-gray-600 hover:text-red-600 transition-colors"
                          title="ဖျက်ရန်"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* --- Office Overlay Modal --- */}
      {isOfficeModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
              <h2 className="text-sm font-bold text-gray-800">
                {editingOffice ? 'ရုံးခွဲ ပြင်ဆင်ရန်' : 'ရုံးခွဲ အသစ်ထည့်ရန်'}
              </h2>
              <button
                onClick={() => setIsOfficeModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200/50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleOfficeSubmit}>
              <div className="p-4 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">ရုံးခွဲ ကုဒ် (Office Code)</label>
                  <input
                    type="text"
                    required
                    value={officeForm.code}
                    onChange={(e) => setOfficeForm({ ...officeForm, code: e.target.value })}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">ရုံးခွဲ အမည်</label>
                  <input
                    type="text"
                    required
                    autoFocus
                    placeholder="ရုံးခွဲ အမည် ထည့်သွင်းပါ..."
                    value={officeForm.name}
                    onChange={(e) => setOfficeForm({ ...officeForm, name: e.target.value })}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">ဌာန (Department)</label>
                  <select
                    value={officeForm.department}
                    onChange={(e) => setOfficeForm({ ...officeForm, department: e.target.value as DepartmentType })}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:border-blue-500"
                  >
                    {DEPARTMENT_OPTIONS.map((dep) => (
                      <option key={dep} value={dep}>
                        {dep}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">အဆင့် (Geo Level)</label>
                  <select
                    value={officeForm.level}
                    onChange={(e) => setOfficeForm({ ...officeForm, level: e.target.value as GeoLevel })}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:border-blue-500"
                  >
                    {Object.values(GeoLevel).map((lvl) => (
                      <option key={lvl} value={lvl}>
                        {lvl}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">ဖုန်းနံပါတ်</label>
                  <input
                    type="text"
                    placeholder="09..."
                    value={officeForm.phone}
                    onChange={(e) => setOfficeForm({ ...officeForm, phone: e.target.value })}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">မြို့နယ် တည်နေရာ ရွေးချယ်ပါ</label>
                  <select
                    value={officeForm.townshipId}
                    onChange={(e) => setOfficeForm({ ...officeForm, townshipId: e.target.value })}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:border-blue-500"
                  >
                    <option value="">-- မြို့နယ် ရွေးချယ်ပါ --</option>
                    {townships.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 p-3 bg-gray-50 border-t border-t-gray-100">
                <button
                  type="button"
                  onClick={() => setIsOfficeModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 font-medium"
                >
                  မလုပ်တော့ပါ
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md text-sm shadow-sm flex items-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingOffice ? 'ပြင်ဆင်မှုသိမ်းဆည်းရန်' : 'အချက်အလက်သိမ်းဆည်းရန်'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Unified Geo Overlay Modal --- */}
      {isGeoModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
              <h2 className="text-sm font-bold text-gray-800">
                {editingGeoItem ? 'ပြင်ဆင်ရန်' : 'အသစ်ထည့်ရန်'} ({LEVEL_LABELS[editingGeoItem?.type || currentLevel]})
              </h2>
              <button
                onClick={() => setIsGeoModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200/50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleGeoFormSubmit}>
              <div className="p-4 space-y-4">
                {!editingGeoItem && currentLevel !== 'state' && (
                  <div className="text-xs text-gray-500 bg-blue-50/50 border border-blue-100 p-2.5 rounded-md">
                    လက်ရှိရွေးချယ်ထားသော တည်နေရာ -{' '}
                    <span className="font-semibold text-blue-700">
                      {currentLevel === 'district' ? selectedState?.name : selectedDistrict?.name}
                    </span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    {LEVEL_LABELS[editingGeoItem?.type || currentLevel]} အမည်
                  </label>
                  <input
                    type="text"
                    required
                    autoFocus
                    placeholder={`${LEVEL_LABELS[editingGeoItem?.type || currentLevel]} အမည် ထည့်သွင်းပါ...`}
                    value={geoNameInput}
                    onChange={(e) => setGeoNameInput(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 p-3 bg-gray-50 border-t border-t-gray-100">
                <button
                  type="button"
                  onClick={() => setIsGeoModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 font-medium"
                >
                  မလုပ်တော့ပါ
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md text-sm shadow-sm flex items-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingGeoItem ? 'ပြင်ဆင်မှုသိမ်းဆည်းရန်' : 'အချက်အလက်သိမ်းဆည်းရန်'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};